import { STATIONS } from "@frontier/content/stations";
import { useEffect, useRef, useState } from "react";
import type { PointerEvent } from "react";
import type { Point } from "./game/geometry";
import type { NavigationState } from "./game/navigation-types";
import { RADAR_VIEWBOX, RadarTap, radarDestination } from "./navigation-ui";
import type {
  MissionView,
  PartyMarker,
  ShipSnapshot,
  WorldSnapshot,
} from "@frontier/protocol/contracts";
import { visibleBeacons } from "./game/beacon-views";
import { MAP_LANDMARKS } from "./map-content";
import { FACTIONS } from "@frontier/content/factions";
import { TRAINING_GATE_FACTIONS } from "@frontier/content/training";
import "./escort.css";

export function Radar({
  world,
  pilot,
  mission,
  partyMarker,
  trackedEventId,
  friends = [],
  trainingExitReady = false,
  navigation,
  onNavigate,
}: {
  navigation: NavigationState;
  onNavigate: (point: Point) => void;
  friends?: readonly string[];
  trainingExitReady?: boolean;
  trackedEventId?: string | null;
  world: WorldSnapshot;
  pilot: ShipSnapshot;
  mission: MissionView | null;
  partyMarker: PartyMarker | null;
}) {
  const [, setClock] = useState(0);
  const [mapTouch] = useState(() => new RadarTap());
  const mapRef = useRef<SVGSVGElement>(null);
  const received = useRef({ world, at: performance.now() });
  if (received.current.world !== world)
    received.current = { world, at: performance.now() };
  useEffect(() => {
    if (!world.beacons?.length) return;
    const timer = setInterval(() => setClock((tick) => tick + 1), 250);
    return () => clearInterval(timer);
  }, [world.beacons]);
  const beaconTime =
    world.time + (performance.now() - received.current.at) / 1000;
  const region = world.regions.find(
    (item) =>
      pilot.x >= item.x &&
      pilot.x < item.x + item.width &&
      pilot.y >= item.y &&
      pilot.y < item.y + item.height,
  );
  const bounds = region ?? {
    x: 0,
    y: 0,
    width: world.width,
    height: world.height,
  };
  const x = (value: number) =>
    (RADAR_VIEWBOX.width * (value - bounds.x)) / bounds.width;
  const y = (value: number) =>
    (RADAR_VIEWBOX.height * (value - bounds.y)) / bounds.height;
  const escortRoute =
    mission && mission.sectorId === region?.id ? mission.terms.escort : null;
  const escortDestination = escortRoute?.path.at(-1);
  useEffect(() => {
    const clear = () => {
      const id = mapTouch.pointerId;
      mapTouch.cancel();
      if (id !== undefined && mapRef.current?.hasPointerCapture(id))
        mapRef.current.releasePointerCapture(id);
    };
    clear();
    window.addEventListener("resize", clear);
    window.addEventListener("blur", clear);
    window.addEventListener("pagehide", clear);
    document.addEventListener("visibilitychange", clear);
    window.visualViewport?.addEventListener("resize", clear);
    window.visualViewport?.addEventListener("scroll", clear);
    return () => {
      window.removeEventListener("resize", clear);
      window.removeEventListener("blur", clear);
      window.removeEventListener("pagehide", clear);
      document.removeEventListener("visibilitychange", clear);
      window.visualViewport?.removeEventListener("resize", clear);
      window.visualViewport?.removeEventListener("scroll", clear);
      clear();
    };
  }, [mapTouch, bounds.x, bounds.y, bounds.width, bounds.height]);
  const pointerPoint = (event: PointerEvent<SVGSVGElement>): Point => ({
    x: event.clientX,
    y: event.clientY,
  });
  const release = (event: PointerEvent<SVGSVGElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const visible = (point: { x: number; y: number }) =>
    point.x >= bounds.x &&
    point.x < bounds.x + bounds.width &&
    point.y >= bounds.y &&
    point.y < bounds.y + bounds.height;
  return (
    <div
      className={`radar ${region?.weather ?? "clear"}`}
      aria-label="Карта сектора"
    >
      <svg
        ref={mapRef}
        className="radar-map"
        viewBox={`0 0 ${RADAR_VIEWBOX.width} ${RADAR_VIEWBOX.height}`}
        aria-description="Коснись точки карты, чтобы задать курс в текущем секторе."
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          const point = pointerPoint(event);
          let destination = radarDestination(
            point,
            event.currentTarget.getScreenCTM(),
            bounds,
          );
          if (trainingExitReady && destination) {
            const picked = destination;
            const nearest = world.gates.filter(gate => TRAINING_GATE_FACTIONS[gate.id] && visible(gate))
              .map(gate => ({ gate, distance: Math.hypot(x(gate.x) - x(picked.x), y(gate.y) - y(picked.y)) }))
              .sort((a, b) => a.distance - b.distance)[0];
            if (nearest && nearest.distance <= 7) destination = { x: nearest.gate.x, y: nearest.gate.y };
          }
          if (mapTouch.begin(event.pointerId, point, destination))
            event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) =>
          mapTouch.move(event.pointerId, pointerPoint(event))
        }
        onPointerUp={(event) => {
          const destination = mapTouch.end(
            event.pointerId,
            pointerPoint(event),
          );
          release(event);
          if (destination) onNavigate(destination);
        }}
        onPointerCancel={(event) => {
          mapTouch.cancel(event.pointerId);
          release(event);
        }}
        onLostPointerCapture={(event) => mapTouch.cancel(event.pointerId)}
        role="img"
        aria-label="Твоя позиция, переходы и текущая цель"
      >
        <rect
          width={RADAR_VIEWBOX.width}
          height={RADAR_VIEWBOX.height}
          fill="transparent"
        />
        <path d="M0 23H100M0 46H100M33 0V70M66 0V70" className="radar-grid" />
        {escortRoute && (
          <g
            aria-label="Маршрут конвоя"
            data-escort-route={escortRoute.routeId}
          >
            <polyline
              className="radar-escort-path"
              points={escortRoute.path
                .map((point) => `${x(point.x)},${y(point.y)}`)
                .join(" ")}
            />
            {escortDestination && (
              <circle
                cx={x(escortDestination.x)}
                cy={y(escortDestination.y)}
                r="1.6"
                className="radar-escort-end"
              />
            )}
          </g>
        )}
        {MAP_LANDMARKS.filter(visible).map((item) => (
          <g key={item.id}>
            <title>{item.label}</title>
            <circle
              cx={x(item.x)}
              cy={y(item.y)}
              r={Math.max(2, (35 * item.size) / bounds.width)}
              className="radar-landmark"
            />
          </g>
        ))}
        {world.obstacles.filter(visible).map((item) => (
          <circle
            key={item.id}
            cx={x(item.x)}
            cy={y(item.y)}
            r={(100 * item.radius) / bounds.width}
            className="radar-rock"
          />
        ))}
        {STATIONS.filter(visible).map((station) => (
          <rect
            key={station.id}
            x={x(station.x) - 2}
            y={y(station.y) - 2}
            width="4"
            height="4"
            className="radar-station"
          />
        ))}
        {world.gates.filter(visible).map((item) => (
          <g key={item.id}>
            <title>{item.name}</title>
            {trainingExitReady && TRAINING_GATE_FACTIONS[item.id] && <circle
              cx={x(item.x)} cy={y(item.y)} r="4.5" className="radar-training-gate-pulse" />}
            <circle
              cx={x(item.x)}
              cy={y(item.y)}
              r="2"
              className={`radar-gate${trainingExitReady && TRAINING_GATE_FACTIONS[item.id] ? " radar-training-gate-ready" : ""}`}
            />
          </g>
        ))}
        {world.publicEvents
          .filter(
            (event) =>
              visible(event.objective) &&
              (event.status === "active" || event.status === "available"),
          )
          .map((event) => (
            <g key={event.id}>
              <title>{event.title}</title>
              {event.capture && (
                <ellipse
                  cx={x(event.x)}
                  cy={y(event.y)}
                  rx={
                    (event.capture.radius * RADAR_VIEWBOX.width) / bounds.width
                  }
                  ry={
                    (event.capture.radius * RADAR_VIEWBOX.height) /
                    bounds.height
                  }
                  fill="none"
                  stroke={
                    event.capture.contested
                      ? "#ee957f"
                      : event.capture.factionId
                        ? FACTIONS[event.capture.factionId].color
                        : "#e4c58f"
                  }
                  strokeWidth="0.7"
                  opacity="0.6"
                />
              )}
              <circle
                cx={x(event.objective.x)}
                cy={y(event.objective.y)}
                r="4"
                className={`radar-event ${event.id === trackedEventId ? "tracked" : ""}`}
              />
            </g>
          ))}
        {world.loot
          .filter(
            (item) =>
              visible(item) &&
              !item.pending &&
              !item.claimedBy.includes(pilot.id),
          )
          .map((item) => (
            <circle
              key={item.id}
              cx={x(item.x)}
              cy={y(item.y)}
              r="1.5"
              className="radar-loot"
            />
          ))}
        {world.npcs
          .filter((item) => visible(item) && !item.dead)
          .map((item) => (
            <circle
              key={item.id}
              cx={x(item.x)}
              cy={y(item.y)}
              r={
                item.kind === "transport"
                  ? 2
                  : item.archetype === "boss"
                    ? 4
                    : item.archetype === "elite"
                      ? 2.5
                      : 1.7
              }
              className={
                item.kind === "transport" ? "radar-transport" : item.kind === "security" && item.securityTargetId !== pilot.id ? "radar-ally" : "radar-enemy"
              }
            />
          ))}
        {visibleBeacons(world.beacons ?? [], beaconTime)
          .filter(visible)
          .map((beacon) => (
            <g key={beacon.id}>
              <title>Примерный район контрабандного сигнала</title>
              <ellipse
                cx={x(beacon.x)}
                cy={y(beacon.y)}
                rx={(100 * beacon.radius) / bounds.width}
                ry={(70 * beacon.radius) / bounds.height}
                className="radar-beacon"
              />
            </g>
          ))}
        {world.players
          .filter(
            (item) => visible(item) && item.id !== pilot.id && !item.docked,
          )
          .map((item) => (
            <circle
              key={item.id}
              cx={x(item.x)}
              cy={y(item.y)}
              r="1.8"
              className={
                friends.includes(item.id) ||
                (pilot.factionId !== null && pilot.factionId === item.factionId)
                  ? "radar-ally"
                  : item.pvpEnabled &&
                      pilot.pvpEnabled &&
                      item.factionId !== null &&
                      pilot.factionId !== null
                    ? "radar-hostile-pilot"
                    : "radar-neutral-pilot"
              }
            >
              <title>
                {item.name} ·{" "}
                {item.factionId ? FACTIONS[item.factionId].name : "Без фракции"}
              </title>
            </circle>
          ))}
        {mission && visible(mission.objective) && (
          <path
            d={`M${x(mission.objective.x)} ${y(mission.objective.y) - 3}l3 3-3 3-3-3Z`}
            className="radar-objective"
          />
        )}
        {partyMarker && visible(partyMarker) && (
          <g
            className="radar-party-marker"
            data-target-id={partyMarker.targetId}
            transform={`translate(${x(partyMarker.x)} ${y(partyMarker.y)})`}
          >
            <title>Группа: {partyMarker.label}</title>
            <circle r="4" />
            <path d="M-6 0H-2M2 0H6M0-6V-2M0 2V6" />
          </g>
        )}
        {navigation.status !== "idle" && visible(navigation.destination) && (
          <g aria-label="Точка курса">
            {navigation.status === "flying" && (
              <polyline
                className="radar-route"
                points={[pilot, ...navigation.path]
                  .filter(
                    (point) =>
                      Number.isFinite(point.x) && Number.isFinite(point.y),
                  )
                  .map((point) => `${x(point.x)},${y(point.y)}`)
                  .join(" ")}
              />
            )}
            <circle
              cx={x(navigation.destination.x)}
              cy={y(navigation.destination.y)}
              r="3"
              className="radar-destination"
            />
          </g>
        )}
        <circle
          cx={x(pilot.x)}
          cy={y(pilot.y)}
          r="2.5"
          className="radar-player"
        />
      </svg>
      <span>{region?.name ?? "ОБЛАСТЬ"}</span>
    </div>
  );
}
